import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from './api';
import type { SignedInUser } from './api';
import { PasswordField } from './password';
import { Button, ErrorBanner, Field, Modal, ModalActions, useAction } from './ui';

/**
 * The signed-in login changes its own password. Any other browser signed in as this
 * login is signed out by the server; this one stays.
 */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} title="Change your password" onClose={onClose}>
      {/* Remounted on each open, so a half-typed password never survives a close. */}
      {open && <ChangePasswordContent onDone={onClose} />}
    </Modal>
  );
}

function ChangePasswordContent({ onDone }: { onDone: () => void }) {
  const [changed, setChanged] = useState(false);

  if (changed) {
    return (
      <>
        <p className="mt-0">
          Your password has been changed. Anywhere else you were signed in as this login has been
          signed out; you stay signed in here.
        </p>
        <ModalActions>
          <Button onClick={onDone}>Done</Button>
        </ModalActions>
      </>
    );
  }

  return <PasswordChangeForm currentLabel="Current password" onChanged={() => setChanged(true)} />;
}

/**
 * Shown instead of the console after signing in with a temporary password from a reset.
 * The server refuses everything else until a new password is chosen, so there is nothing
 * behind this page to reach: it is the whole app until then. A page, not a dialog, for
 * that reason - there is nothing to close it back to.
 */
export function ChooseNewPassword({
  user,
  onChanged,
  onSignOut,
}: {
  user: SignedInUser;
  onChanged: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="w-[min(400px,100%)] rounded-xl border border-divider bg-raised p-5">
        <img src="/icon-mark.png" alt="" width={32} height={56} className="mb-3 h-14 w-auto" />
        <h1 className="mb-1 text-[22px] font-semibold">Choose a new password</h1>
        <p className="mt-0 mb-4 text-muted">
          You signed in as <strong className="text-text">{user.email}</strong> with a temporary
          password. Choose your own to carry on.
        </p>
        <PasswordChangeForm
          currentLabel="Temporary password"
          submitLabel="Save and continue"
          onChanged={onChanged}
        />
        <Button variant="link" className="mt-4" onClick={onSignOut}>
          Sign out instead
        </Button>
      </div>
    </div>
  );
}

function PasswordChangeForm({
  currentLabel,
  submitLabel = 'Change password',
  onChanged,
}: {
  currentLabel: string;
  submitLabel?: string;
  onChanged: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const action = useAction();

  const mismatch = confirm.length > 0 && confirm !== next;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      action.setError('The two new passwords do not match');
      return;
    }
    const ok = await action.run(() => api.auth.changePassword(current, next));
    if (ok) onChanged();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />
      <Field label={currentLabel}>
        <input
          type="password"
          required
          autoFocus
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </Field>
      <Field label="New password" hint="At least 8 characters, or generate one.">
        <PasswordField
          value={next}
          onChange={setNext}
          // A generated password goes in both boxes: the point of generating is not
          // having to type it, twice or once.
          onGenerated={setConfirm}
        />
      </Field>
      <Field label="New password again" hint={mismatch ? 'Does not match yet.' : undefined}>
        <input
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </Field>
      <ModalActions>
        <Button type="submit" disabled={action.busy || mismatch}>
          {action.busy ? 'Saving…' : submitLabel}
        </Button>
      </ModalActions>
    </form>
  );
}
